/* eslint-disable simple-import-sort/imports */
import { useMemo, useState } from "react";
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useParams } from "react-router-dom";
import useAsyncEffect from "use-async-effect";
import "./DocumentView.css";
import OpenApiClient from "../../../../packages/fxid-openapi-client/src/OpenApiClient.ts";
import { FxIdApplicationHandlersDocumentsGetDocumentDocumentDto } from "@fxid/openapi-client";

export const DocumentView = () => {
	const { game, id } = useParams<{ game: string; id: string }>();

	const [document, setDocument] = useState<FxIdApplicationHandlersDocumentsGetDocumentDocumentDto | null>(null);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const [isAccepting, setIsAccepting] = useState(false);
	const [isAccepted, setIsAccepted] = useState(false);

	useAsyncEffect(async () => {
		if (!game || !id) {
			setError("Game or Document ID is missing.");
			setLoading(false);
			return;
		}
		try {
			const result = await OpenApiClient.Games.fxIdWebFeaturesDocumentsGetDocumentEndpointGetDocumentEndpoint(game, Number(id));
			setDocument(result);

			if (result && result.IsAccepted) {
				setIsAccepted(result.IsAccepted);
			}
		} catch (e) {
			log.error("Failed to fetch document:", e); // Используйте console.error или ваш логгер
			setError("Document not found or an error occurred.");
		} finally {
			setLoading(false);
		}
	}, [game, id]);

	const currentVersion = useMemo(() => {
		return document?.LatestVersion ?? null;
	}, [document]);

	const hasMarkdownContent = useMemo(() => {
		return !!currentVersion?.Content?.Value;
	}, [currentVersion]);

	const sanitizedHtmlContent = useMemo(() => {
		if (!hasMarkdownContent || !currentVersion?.Content?.Value) {
			return "";
		}
		const rawHtml = marked.parse(currentVersion.Content.Value) as string;
		return DOMPurify.sanitize(rawHtml);
	}, [hasMarkdownContent, currentVersion]);

	const showAcceptButton = useMemo(() => {
		if (!document || !document.DocumentType) return false;
		const acceptanceRequired = document.DocumentType.NeedUserAcceptance;
		return acceptanceRequired && !isAccepted;
	}, [document, isAccepted]);

	const handleAccept = async () => {
		if (!document || !currentVersion) return;

		setIsAccepting(true);
		try {
			await OpenApiClient.Games.fxIdWebFeaturesDocumentsAcceptDocumentAcceptDocumentEndpoint({
				DocumentId: document.Id,
				DocumentVersionId: document.LatestVersion!.Id
			});

			setIsAccepted(true);
		} catch (e) {
			log.error("Accept failed", e);
			alert("Failed to accept document. Please try again.");
		} finally {
			setIsAccepting(false);
		}
	};

	if (isLoading) return <div className="document-view-container"><div className="loader">Loading...</div></div>;
	if (error || !document) return <div className="document-view-container"><p className="error">{error || "Document not found."}</p></div>;
	if (!currentVersion) return <div className="document-view-container"><p>No active version for this document.</p></div>;

	const dateStr = currentVersion.CreatedAt
		? new Date(currentVersion.CreatedAt).toLocaleString()
		: "Unknown date";

	return (
		<div className="document-view-container">
			<header className="document-header">
				<h1>{document.DocumentType.Value}</h1>
				<p className="document-meta">
					Last updated: {dateStr}
				</p>
			</header>

			<hr />

			<main className="document-content">
				{hasMarkdownContent ? (
					<div dangerouslySetInnerHTML={{ __html: sanitizedHtmlContent }} />
				) : (
					currentVersion.Url ? (
						<div className="iframe-container">
							<div className="external-link-warning">
								<p>
									If the document does not load below, please
									<a href={currentVersion.Url} target="_blank" rel="noopener noreferrer"> click here to view it</a>.
								</p>
							</div>
							<iframe
								src={currentVersion.Url}
								title="Document Content"
								className="document-iframe"
								sandbox="allow-scripts allow-same-origin allow-popups"
							/>
						</div>
					) : (
						<p>Document content is empty.</p>
					)
				)}
			</main>

			<footer className="document-actions">
				{isAccepted ? (
					<div className="accepted-message">
						<span>&#10003;</span> You have accepted this document.
					</div>
				) : showAcceptButton && (
					<button
						className="accept-button"
						onClick={handleAccept}
						disabled={isAccepting}
					>
						{isAccepting ? "Processing..." : "I Accept Terms & Conditions"}
					</button>
				)}
			</footer>
		</div>
	);
};
